import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { Canon } from '@models/Canon';
import { ParsingFormat } from '@models/parsing-formats/ParsingFormat';
import { ProjectConfiguration } from '@models/ProjectConfiguration';
import { ParsingFormatEditorComponent, ParsingFormatEditorInput } from '../parsing-format-editor/parsing-format-editor.component';

type CanonList = {
  canon: Canon;
  parsingFormats: FormControl<ParsingFormat>[];
};

@Component({
  selector: 'app-parsing-formats',
  imports: [
    MatCardModule,
    MatListModule,
    MatButtonModule
  ],
  templateUrl: './parsing-formats.component.html',
  styleUrl: './../project-settings.component.scss'
})
export class ParsingFormatsComponent implements OnInit {
  @Input() project!: ProjectConfiguration;
  @Input() formGroup!: FormGroup;
  @Input() resetEvent!: EventEmitter<void>;

  constructor(public dialog: MatDialog, private fb: FormBuilder) { }

  ngOnInit(): void {
    this.project.canons.forEach((c) => {
      const controls: FormControl[] = [];
      this.project.parsingFormats.getParsingFormatsForCanon(c)?.forEach((pf) => {
        controls.push(new FormControl<ParsingFormat>(pf));
      });
      this.formGroup.addControl(c, this.fb.array(controls));
    });

    this.resetEvent.subscribe(() => {
      this.project.canons.forEach((c) => {
        const controls: FormControl[] = [];
        this.project.parsingFormats.getParsingFormatsForCanon(c)?.forEach((pf) => {
          controls.push(new FormControl<ParsingFormat>(pf));
        });
        this.formGroup.setControl(c, this.fb.array(controls));
      });
    });
  }

  objectEntries = Object.entries;

  get canonArrays(): CanonList[] {
    return Object.keys(this.formGroup.controls).map(key => ({
      canon: key as Canon,
      parsingFormats: (this.formGroup.get(key) as FormArray).controls as FormControl<ParsingFormat>[]
    }));
  }

  newParsingFormat(canon: Canon) {
    const dialogRef =
      this.dialog.open<ParsingFormatEditorComponent, ParsingFormatEditorInput, ParsingFormat>(ParsingFormatEditorComponent, {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        panelClass: 'full-screen-dialog',
        data: {
          canon: canon,
          project: this.project
        }
      });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        (this.formGroup.get(canon) as FormArray).push(new FormControl<ParsingFormat>(result));
      }
    });
  }

  editParsingFormat(c: Canon, pf: ParsingFormat) {
    const oldId = pf.id;
    const dialogRef = this.dialog.open<ParsingFormatEditorComponent, ParsingFormatEditorInput, ParsingFormat>(ParsingFormatEditorComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'full-screen-dialog',
      data: {
        canon: c,
        project: this.project,
        parsingFormat: pf
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const pf = result;
        const pfArray = this.formGroup.get(c) as FormArray;
        const index = pfArray.controls.findIndex((control) => (control as FormControl<ParsingFormat>).value.id === oldId);
        if (index !== -1) {
          pfArray.setControl(index, new FormControl<ParsingFormat>(pf));
        }
      }
    });
  }

  removeParsingFormat(c: Canon, pf: ParsingFormat) {
    const pfArray = this.formGroup.get(c) as FormArray;
    const index = pfArray.controls.findIndex((control) => (control as FormControl<ParsingFormat>).value.id === pf.id);
    if (index !== -1) {
      pfArray.removeAt(index);
    }
  }

}
